'use client';

import React, { useState } from 'react';
import { Calendar, ChevronDown, Filter } from 'lucide-react';

const dateRanges = [
  { id: 'dr-today', label: 'Today' },
  { id: 'dr-yesterday', label: 'Yesterday' },
  { id: 'dr-7d', label: 'Last 7 days' },
  { id: 'dr-30d', label: 'Last 30 days' },
  { id: 'dr-curr-month', label: 'Current month' },
  { id: 'dr-prev-month', label: 'Previous month' },
  { id: 'dr-custom', label: 'Custom range' },
];

const warehouses = [
  { id: 'wh-sanpka', label: 'Sanpka, Haryana', short: 'SNP' },
  { id: 'wh-malur', label: 'Malur, Karnataka', short: 'MLR' },
  { id: 'wh-uluberia', label: 'Uluberia, W.B.', short: 'ULB' },
  { id: 'wh-bhiwandi', label: 'Bhiwandi, MH', short: 'BHW' },
];

const brands = [
  { id: 'brand-lava', label: 'Lava', color: 'bg-blue-100 text-blue-700' },
  { id: 'brand-moto', label: 'Motorola', color: 'bg-orange-100 text-orange-700' },
];

export default function DashboardFilters() {
  const [selectedRange, setSelectedRange] = useState('dr-30d');
  const [selectedWarehouses, setSelectedWarehouses] = useState<string[]>(['wh-sanpka', 'wh-malur', 'wh-uluberia', 'wh-bhiwandi']);
  const [selectedBrands, setSelectedBrands] = useState<string[]>(['brand-lava', 'brand-moto']);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showWarehousePicker, setShowWarehousePicker] = useState(false);

  const toggleWarehouse = (id: string) => {
    setSelectedWarehouses((prev) =>
      prev.includes(id) ? prev.filter((w) => w !== id) : [...prev, id]
    );
  };

  const toggleBrand = (id: string) => {
    setSelectedBrands((prev) =>
      prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id]
    );
  };

  const selectedRangeLabel = dateRanges.find((d) => d.id === selectedRange)?.label || 'Select range';

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <Filter size={14} className="text-muted-foreground" />

      {/* Date Range */}
      <div className="relative">
        <button
          onClick={() => { setShowDatePicker(!showDatePicker); setShowWarehousePicker(false); }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-card border border-border rounded-lg text-xs font-500 text-foreground hover:bg-muted transition-colors duration-150 btn-press"
        >
          <Calendar size={13} className="text-primary" />
          {selectedRangeLabel}
          <ChevronDown size={12} className="text-muted-foreground" />
        </button>
        {showDatePicker && (
          <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-xl shadow-dropdown z-40 py-1 min-w-40 fade-in">
            {dateRanges.map((dr) => (
              <button
                key={dr.id}
                onClick={() => { setSelectedRange(dr.id); setShowDatePicker(false); }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-muted transition-colors ${
                  selectedRange === dr.id ? 'text-primary font-600 bg-info-bg' : 'text-foreground'
                }`}
              >
                {dr.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Warehouse Filter */}
      <div className="relative">
        <button
          onClick={() => { setShowWarehousePicker(!showWarehousePicker); setShowDatePicker(false); }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-card border border-border rounded-lg text-xs font-500 text-foreground hover:bg-muted transition-colors duration-150 btn-press"
        >
          <span className="text-muted-foreground">Warehouses:</span>
          <span className="text-primary">{selectedWarehouses.length === 4 ? 'All (4)' : `${selectedWarehouses.length} selected`}</span>
          <ChevronDown size={12} className="text-muted-foreground" />
        </button>
        {showWarehousePicker && (
          <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-xl shadow-dropdown z-40 py-2 min-w-52 fade-in">
            {warehouses.map((wh) => (
              <label key={wh.id} className="flex items-center gap-2 px-3 py-2 hover:bg-muted cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedWarehouses.includes(wh.id)}
                  onChange={() => toggleWarehouse(wh.id)}
                  className="accent-primary"
                />
                <span className="text-xs font-500 text-foreground">{wh.label}</span>
                <span className="ml-auto text-2xs text-muted-foreground font-mono">{wh.short}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Brand Toggles */}
      {brands.map((brand) => (
        <button
          key={brand.id}
          onClick={() => toggleBrand(brand.id)}
          className={`px-3 py-1.5 rounded-lg text-xs font-600 border transition-all duration-150 btn-press ${
            selectedBrands.includes(brand.id)
              ? brand.color + 'border-transparent' :'bg-card border-border text-muted-foreground'
          }`}
        >
          {brand.label}
        </button>
      ))}

      <div className="ml-auto flex items-center gap-2">
        <span className="text-2xs text-muted-foreground">
          Demo data — 04 Aug 2026
        </span>
        <span className="text-2xs bg-warning-bg text-warning px-2 py-0.5 rounded-full font-500">SAMPLE DATA</span>
      </div>
    </div>
  );
}